import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { CreateJournalEntryInput } from 'shared';

import { JournalEntryEditor } from '../components/domain/JournalEntryEditor';
import { JournalTimeline } from '../components/domain/JournalTimeline';
import { TopStatusBar } from '../components/domain/TopStatusBar';
import { Card } from '../components/ui/Card';
import { useAccounts } from '../features/accounts/queries';
import { useMe } from '../features/auth/queries';
import { useUpdateJournalEntry } from '../features/journal/mutations';
import { useJournalEntry } from '../features/journal/queries';
import { useLatestSnapshot } from '../features/snapshots/queries';
import type { JournalEntryDto } from '../lib/api/endpoints';

export function JournalEntry() {
  const params = useParams();
  const id = params.id ?? '';
  const me = useMe();
  const accounts = useAccounts();
  const activeAccountId = accounts.data?.[0]?.id ?? null;
  const snapshot = useLatestSnapshot(activeAccountId);
  const timeline = useJournalEntry(id);
  const updateEntry = useUpdateJournalEntry();
  const [editing, setEditing] = useState<JournalEntryDto | null>(null);

  if (me.isLoading || timeline.isLoading) {
    return <main className="p-6 text-text-muted">Loading journal entry...</main>;
  }
  if (me.isError || !me.data || timeline.isError) {
    return <main className="p-6 text-danger">Could not load journal entry.</main>;
  }

  return (
    <main className="min-h-screen bg-bg text-text">
      <TopStatusBar currency={me.data.baseCurrency} snapshot={snapshot.data} />
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[minmax(0,1fr)_420px]">
        <Card header={<h1 className="text-2xl font-semibold">Trade Timeline</h1>}>
          <JournalTimeline entries={timeline.data ?? []} onEdit={setEditing} />
        </Card>
        <Card header={<h2 className="text-lg font-semibold">Edit Entry</h2>}>
          {editing ? (
            <JournalEntryEditor
              entry={editing}
              isSubmitting={updateEntry.isPending}
              onSubmit={(input: CreateJournalEntryInput) =>
                updateEntry.mutate({ id: editing.id, data: input })
              }
            />
          ) : (
            <p className="text-sm text-text-muted">Choose an entry from the timeline.</p>
          )}
        </Card>
      </div>
    </main>
  );
}
